<template>
  <!-- data-testid: the visual-regression suite drives this control to unlock selection-gated
       screens, and there is no accessible-name hook on the group itself — CustomIonLabel only
       renders when `inputLabel` is passed, which several call sites omit. Same rationale as the
       existing [data-visual-mask] hook. See tests/e2e/visual/_support/interactions.ts. -->
  <div class="custom-ion-radio" :data-testid="`radio-${inputId}`" :class="'size-' + size">
    <CustomIonLabel
      v-if="inputLabel"
      :inputLabel="inputLabel"
      :inputId="inputId"
      :isMandatory="isMandatory"
      :size="size"
    />
    <CustomIonSearchbar
      v-if="isSearchable && !isDisabled"
      ref="searchbarRef"
      :placeholder="`Search for a ${searchField}`"
      @onSearch="onSearch"
      class="mb-3"
    />
    <IonRadioGroup
      :value="inputValue"
      :allow-empty-selection="allowEmptySelection"
      @ionChange="onChange"
    >
      <div :class="isVerticalMode ? 'flex flex-col' : 'flex flex-row flex-wrap gap-1'">
        <template v-if="isVerticalMode">
          <IonRadio
            v-for="radioItem in displayedItems"
            :key="radioItem.Id"
            :value="radioItem.Id"
            :disabled="isDisabled || radioItem.IsDisabled"
            :labelPlacement="'start'"
            class="w-full px-2 border-b control-list-row"
            :class="
              inputValue === radioItem.Id
                ? 'bg-color-primary-light border-color-primary color-primary'
                : 'color-text'
            "
          >
            <span class="flex items-center gap-1.5 w-full">
              <i
                v-if="radioItem.IconName"
                :class="`ti ti-${radioItem.IconName}`"
                class="shrink-0"
                aria-hidden="true"
              ></i>
              <span class="flex-1 min-w-0 text-left">{{ radioItem.Name }}</span>
              <i
                v-show="inputValue === radioItem.Id"
                class="ti ti-check shrink-0"
                aria-hidden="true"
              ></i>
            </span>
          </IonRadio>
          <LazyLoadingViewer
            v-if="hasMore"
            :key="`load-more-${displayedItemCount}`"
            @onVisible="loadMore"
          >
            <template #content>
              <span />
            </template>
            <template #placeholder>
              <SkeletonShimmerBase>
                <SkeletonItem variant="rect" width="100%" height="var(--size-touch)" />
              </SkeletonShimmerBase>
            </template>
          </LazyLoadingViewer>
        </template>
        <template v-else>
          <IonRadio
            v-for="radioItem in processedItems"
            v-show="radioItem.IsVisible"
            :key="radioItem.Id"
            :value="radioItem.Id"
            :disabled="isDisabled || radioItem.IsDisabled"
            labelPlacement="start"
            class="toggle-pill-base font-normal max-w-full min-w-0"
            :class="isDisabled || radioItem.IsDisabled ? 'opacity-75' : ''"
          >
            <span class="flex items-center gap-1.5 w-full">
              <i
                v-if="radioItem.IconName"
                :class="`ti ti-${radioItem.IconName}`"
                class="shrink-0"
                aria-hidden="true"
              ></i>
              <span class="flex-1 min-w-0 text-left">{{ radioItem.Name }}</span>
              <i
                v-show="inputValue === radioItem.Id"
                class="ti ti-check shrink-0"
                aria-hidden="true"
              ></i>
            </span>
          </IonRadio>
        </template>
      </div>
    </IonRadioGroup>
    <NoRecordFound v-if="isAllItemsHidden" />
    <div v-if="isFormField" class="validation-slot">
      <Field v-model="inputValue" :id="inputId" :name="inputId" hidden />
      <ErrorMessage :name="inputId" class="validation-message" />
    </div>
  </div>
</template>

<script lang="ts">
import CustomIonLabel from "@/components/inputs/CustomIonLabel.vue";
import CustomIonSearchbar from "@/components/ionic/CustomIonSearchbar.vue";
import NoRecordFound from "@/components/states/NoRecordFound.vue";
import LazyLoadingViewer from "@/components/widgets/LazyLoadingViewer.vue";
import SkeletonItem from "@/components/skeletons/SkeletonItem.vue";
import SkeletonShimmerBase from "@/components/skeletons/SkeletonShimmerBase.vue";
import { handleCheckboxSearch } from "@/methods/input";
import { deepCopyObject } from "@/methods/utils";
import type { ComponentSizeType } from "@/types/componentType";
import { InputOption } from "@/types/global";
import { IonRadio, IonRadioGroup } from "@ionic/vue";
import { ErrorMessage, Field } from "vee-validate";
import { PropType, computed, defineComponent, onBeforeMount, ref } from "vue";

export default defineComponent({
  name: "CustomIonRadioButton",
  components: {
    CustomIonLabel,
    CustomIonSearchbar,
    IonRadioGroup,
    IonRadio,
    NoRecordFound,
    LazyLoadingViewer,
    SkeletonShimmerBase,
    SkeletonItem,
    ErrorMessage,
    Field,
  },
  props: {
    inputId: { type: String, required: true },
    radioItems: { type: Array as PropType<Array<InputOption>>, required: true },
    initialValue: { type: [Number, String, null], default: null },
    inputLabel: { type: String, required: false },
    allowEmptySelection: { type: Boolean, default: false },
    isMandatory: { type: Boolean, default: false },
    isSearchable: { type: Boolean, default: false },
    searchField: { type: String, default: "value" },
    isFormField: { type: Boolean, default: false },
    isVerticalMode: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false },
    size: { type: String as PropType<ComponentSizeType>, default: "default" },
  },
  setup(props, { emit }) {
    const CHUNK_SIZE = 50;
    const inputValue = ref(props.initialValue);
    const processedItems = ref<Array<InputOption>>();
    const searchedValue = ref("");
    const displayedItemCount = ref(CHUNK_SIZE);
    const searchbarRef = ref<InstanceType<typeof CustomIonSearchbar> | null>(null);

    const isAllItemsHidden = computed(() => {
      if (!(processedItems.value && processedItems.value.length > 0)) {
        return true;
      }
      return !processedItems.value.some((item) => item.IsVisible);
    });

    const displayedItems = computed(() => {
      if (!processedItems.value) {
        return [];
      }
      if (searchedValue.value) {
        return processedItems.value.filter((item) => item.IsVisible);
      }
      return processedItems.value.slice(0, displayedItemCount.value);
    });

    const hasMore = computed(() => {
      return !searchedValue.value && displayedItemCount.value < (processedItems.value?.length ?? 0);
    });

    onBeforeMount(() => {
      processedItems.value = deepCopyObject(props.radioItems) as Array<InputOption>;
      handleCheckboxSearch(processedItems.value, "");
    });

    function onChange(event: CustomEvent) {
      inputValue.value = event.detail.value;
      emit("onChange", inputValue.value);
    }

    function onSearch(value: string) {
      if (!value && searchedValue.value) {
        displayedItemCount.value = CHUNK_SIZE;
      }
      searchedValue.value = value;
      handleCheckboxSearch(processedItems.value, value);
    }

    function loadMore() {
      displayedItemCount.value += CHUNK_SIZE;
    }

    // Called by an owning overlay (CustomIonSelect) once its enter transition settles, so the
    // search field is ready to type into. No-ops when the searchbar isn't rendered.
    async function focusSearch(): Promise<void> {
      await searchbarRef.value?.setFocus();
    }

    return {
      isAllItemsHidden,
      processedItems,
      displayedItems,
      displayedItemCount,
      hasMore,
      inputValue,
      searchbarRef,
      onChange,
      onSearch,
      loadMore,
      focusSearch,
    };
  },
});
</script>

<style lang="scss">
.custom-ion-radio {
  ion-radio {
    --color: var(--color-medium);
    --toggle-pill-height: calc(var(--control-height) - (var(--control-pad-y-small) * 2) - 2px);
    --toggle-pill-font-size: var(--control-font);
    --toggle-pill-padding: var(--control-pad-y-small) var(--pill-pad-x);
    &.toggle-pill-base.radio-checked {
      border-color: var(--color-primary) !important;
      background-color: var(--color-primary-bg-light) !important;
      color: var(--color-primary) !important;
    }
    &.toggle-pill-base::part(label) {
      white-space: normal;
      overflow-wrap: anywhere;
    }
    /* Hide the native radio mark — the slotted Tabler "ti-check" renders the tick instead */
    &.toggle-pill-base::part(container),
    &.control-list-row::part(container) {
      display: none;
    }
  }
  &.size-small {
    font-size: var(--control-font-small);
    --size: var(--font-size-s);
  }
  &.size-large {
    font-size: var(--control-font-large);
    --size: var(--font-size-l);
  }
}
</style>
